"use client";

import { useEffect, useRef } from "react";
import {
  fireAndForgetAppLog,
  getBrowserAppLogContext,
} from "@/lib/api/services/app_logs.client";
import type {
  AppLogEventType,
  AppLogLevel,
  AppLogSource,
} from "@/types/app-log-types";
import { getAppLogErrorDetails } from "@/lib/utils/app-log-error";

type AppLogPageEventProps = {
  level: AppLogLevel;
  eventType: AppLogEventType;
  source: AppLogSource;
  statusCode: number;
  sameOriginReferrerOnly?: boolean;
  tags?: string[];
};

export default function AppLogPageEvent({
  level,
  eventType,
  source,
  statusCode,
  sameOriginReferrerOnly = false,
  tags = [],
}: AppLogPageEventProps) {
  const hasLogged = useRef(false);

  useEffect(() => {
    if (hasLogged.current) return;
    hasLogged.current = true;

    if (sameOriginReferrerOnly) {
      try {
        if (
          !document.referrer ||
          new URL(document.referrer).origin !== window.location.origin
        ) {
          return;
        }
      } catch {
        return;
      }
    }

    const path = `${window.location.pathname}${window.location.search}`;
    const errorMessage =
      statusCode === 404
        ? `Page not found: ${path}`
        : `Page returned status ${statusCode}: ${path}`;
    const errorDetails = getAppLogErrorDetails(undefined, {
      name: statusCode === 404 ? "PageNotFoundError" : "PageStatusError",
      message: errorMessage,
      digestParts: [eventType, source, statusCode, path],
    });

    fireAndForgetAppLog({
      ...getBrowserAppLogContext(),
      level,
      event_type: eventType,
      source,
      status_code: statusCode,
      ...errorDetails,
      fingerprint: `${statusCode}:${path}`,
      tags,
    });
  }, [eventType, level, sameOriginReferrerOnly, source, statusCode, tags]);

  return null;
}
